export const MESSENGER_DRAFT_SAVE_DEBOUNCE_MS = 1500;
export const MESSENGER_DRAFT_RETRY_BACKOFF_MS = 250;

export type ComposerDraftSnapshot = {
  text: string;
  reply_to_message_id: string | null;
  forward_message_ids: string[];
};

export function isComposerDraftSnapshotEmpty(snapshot: ComposerDraftSnapshot): boolean {
  if (snapshot.reply_to_message_id !== null) return false;
  if (snapshot.forward_message_ids.length > 0) return false;
  return snapshot.text.trim().length === 0;
}

export function shouldApplyRemoteDraftToComposer(args: {
  isComposerFocused: boolean;
  isEditingMessage: boolean;
  localRevisionMs: number;
  remoteUpdatedAtMs: number;
}): boolean {
  if (args.isComposerFocused) return false;
  if (args.isEditingMessage) return false;
  if (args.localRevisionMs > args.remoteUpdatedAtMs) return false;
  return true;
}

type MessengerDraftSaveSchedulerOptions = {
  debounceMs?: number;
  save: (snapshot: ComposerDraftSnapshot, clientUpdatedAt: string) => Promise<void>;
  clear: (clientUpdatedAt: string) => Promise<void>;
};

function isTransientDraftPersistenceError(error: unknown): boolean {
  if (error instanceof TypeError) return true;
  if (typeof error !== 'object' || error === null || !('status' in error)) return false;
  const status = (error as { status?: unknown }).status;
  return typeof status === 'number' && status >= 500;
}

async function waitForDraftRetryBackoff(): Promise<void> {
  await new Promise<void>((resolve) => {
    setTimeout(resolve, MESSENGER_DRAFT_RETRY_BACKOFF_MS);
  });
}

export class MessengerDraftSaveScheduler {
  private readonly debounceMs: number;
  private readonly save: MessengerDraftSaveSchedulerOptions['save'];
  private readonly clear: MessengerDraftSaveSchedulerOptions['clear'];
  private timer: ReturnType<typeof setTimeout> | null = null;
  private latestSnapshot: ComposerDraftSnapshot | null = null;
  private revision = '';
  private persistPromise: Promise<void> = Promise.resolve();

  constructor(opts: MessengerDraftSaveSchedulerOptions) {
    this.debounceMs = opts.debounceMs ?? MESSENGER_DRAFT_SAVE_DEBOUNCE_MS;
    this.save = opts.save;
    this.clear = opts.clear;
  }

  schedule(snapshot: ComposerDraftSnapshot): string {
    this.revision = new Date().toISOString();
    this.latestSnapshot = snapshot;
    this.cancelTimer();
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.persistLatestSnapshot();
    }, this.debounceMs);
    return this.revision;
  }

  async flush(): Promise<void> {
    this.cancelTimer();
    await this.persistLatestSnapshot();
  }

  async discard(): Promise<void> {
    this.cancelTimer();
    this.latestSnapshot = null;
    this.revision = '';
    await this.persistPromise;
  }

  dispose(): void {
    void this.flush();
  }

  private cancelTimer(): void {
    if (this.timer === null) return;
    clearTimeout(this.timer);
    this.timer = null;
  }

  private async persistLatestSnapshot(): Promise<void> {
    if (this.latestSnapshot === null) {
      await this.persistPromise;
      return;
    }

    const snapshot = this.latestSnapshot;
    const revision = this.revision;
    this.latestSnapshot = null;
    this.revision = '';

    this.persistPromise = this.persistPromise.then(async () => {
      if (isComposerDraftSnapshotEmpty(snapshot)) {
        await this.runBestEffort(() => this.clear(revision));
        return;
      }

      await this.runBestEffort(() => this.save(snapshot, revision));
    });
    await this.persistPromise;
  }

  private async runBestEffort(operation: () => Promise<void>): Promise<void> {
    try {
      await operation();
    } catch (error) {
      if (!isTransientDraftPersistenceError(error)) return;
      await waitForDraftRetryBackoff();
      try {
        await operation();
      } catch {
        // Draft persistence remains best-effort after one transient retry.
      }
    }
  }
}
